import React, { useState, useEffect, useMemo } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Alert } from 'reactstrap'
import Select from 'react-select'
import { useMutation, useQuery } from '@apollo/client'
import { CREATE_CHANNEL, UPDATE_CHANNEL } from './Mutations'
import { GET_ADMIN_INBOXES } from './Queries'

const CHANNEL_TYPES = [
    { value: 'twilio_sms', label: 'Twilio SMS' },
    { value: 'twilio_whatsapp', label: 'Twilio WhatsApp' },
    { value: 'mobile_app_chat', label: 'Mobile App Chat' }
]

export default function CreateEditChannelModal({ isOpen, toggle, channel, onSuccess }) {
    const [inboxId, setInboxId] = useState('')
    const [name, setName] = useState('')
    const [channelType, setChannelType] = useState('twilio_sms')
    const [sendAs, setSendAs] = useState('')
    const [twilioSid, setTwilioSid] = useState('')
    const [twilioAuthToken, setTwilioAuthToken] = useState('')
    const [errors, setErrors] = useState([])

    const isEdit = !!channel

    const { data: inboxesData } = useQuery(GET_ADMIN_INBOXES, {
        variables: { isArchived: false }
    })

    const inboxes = useMemo(
        () => inboxesData?.myAdminInboxes?.edges?.map(edge => edge.node) || [],
        [inboxesData]
    )

    useEffect(() => {
        if (channel) {
            setInboxId(channel.inbox?.id || '')
            setName(channel.name || '')
            setChannelType(channel.channelType || 'twilio_sms')
            setSendAs(channel.sendAs || '')
            setTwilioSid('')
            setTwilioAuthToken('')
        } else {
            setInboxId(inboxes.length > 0 ? inboxes[0].id : '')
            setName('')
            setChannelType('twilio_sms')
            setSendAs('')
            setTwilioSid('')
            setTwilioAuthToken('')
        }
        setErrors([])
    }, [channel, isOpen, inboxes])

    const [createChannel, { loading: creating }] = useMutation(CREATE_CHANNEL, {
        onCompleted: (data) => {
            if (data.messagingCreateChannel.ok) {
                const createdName = data.messagingCreateChannel.channel?.name || name.trim()
                toggle()
                if (onSuccess) onSuccess(`Channel "${createdName}" created successfully.`)
            } else {
                setErrors(data.messagingCreateChannel.errors || ['Failed to create channel'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const [updateChannel, { loading: updating }] = useMutation(UPDATE_CHANNEL, {
        onCompleted: (data) => {
            if (data.messagingUpdateChannel.ok) {
                const updatedName = data.messagingUpdateChannel.channel?.name || name.trim()
                toggle()
                if (onSuccess) onSuccess(`Channel "${updatedName}" updated successfully.`)
            } else {
                setErrors(data.messagingUpdateChannel.errors || ['Failed to update channel'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const handleSubmit = (e) => {
        e.preventDefault()
        setErrors([])

        if (!name.trim()) {
            setErrors(['Name is required'])
            return
        }

        if (!sendAs.trim()) {
            setErrors(['Send As phone number is required'])
            return
        }

        // Basic E.164 format validation
        if (!sendAs.match(/^\+[1-9]\d{1,14}$/)) {
            setErrors(['Send As must be in E.164 format (e.g., +1234567890)'])
            return
        }

        if (isEdit) {
            const variables = {
                id: channel.id,
                name: name.trim(),
                sendAs: sendAs.trim()
            }
            if (twilioSid.trim()) variables.twilioSid = twilioSid.trim()
            if (twilioAuthToken.trim()) variables.twilioAuthToken = twilioAuthToken.trim()

            updateChannel({ variables })
        } else {
            if (!inboxId) {
                setErrors(['Please select an inbox'])
                return
            }

            createChannel({
                variables: {
                    inboxId,
                    name: name.trim(),
                    channelType,
                    sendAs: sendAs.trim(),
                    twilioSid: twilioSid.trim() || null,
                    twilioAuthToken: twilioAuthToken.trim() || null
                }
            })
        }
    }

    const loading = creating || updating

    return (
        <Modal isOpen={isOpen} size="lg">
            <ModalHeader>
                {isEdit ? 'Edit Channel' : 'Create New Channel'}
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {errors.length > 0 && (
                        <Alert color="danger">
                            {errors.map((error, idx) => (
                                <div key={idx}>{error}</div>
                            ))}
                        </Alert>
                    )}

                    {!isEdit && (
                        <FormGroup>
                            <Label for="channelInbox">Inbox *</Label>
                            <Select className="bos-custom-select"
                                classNamePrefix="bos-select"
                                isLoading={false}
                                options={
                                    inboxes.map((inbox) => ({ value: inbox.id, label: inbox.name }))
                                }
                                placeholder="Select inbox for channel"
                                isMulti={false}
                                onChange={(option) => setInboxId(option.value)}
                            />
                        </FormGroup>
                    )}

                    <FormGroup>
                        <Label for="channelName">Name *</Label>
                        <Input
                            type="text"
                            id="channelName"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter channel name"
                            disabled={loading}
                            required
                        />
                    </FormGroup>

                    {!isEdit && (
                        <FormGroup>
                            <Label for="channelType">Channel Type *</Label>
                            <Input
                                type="select"
                                id="channelType"
                                value={channelType}
                                onChange={(e) => setChannelType(e.target.value)}
                                disabled={loading}
                                required
                            >
                                {CHANNEL_TYPES.map((type) => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    )}

                    <FormGroup>
                        <Label for="channelSendAs">Send As (E.164 format) *</Label>
                        <Input
                            type="text"
                            id="channelSendAs"
                            value={sendAs}
                            onChange={(e) => setSendAs(e.target.value)}
                            placeholder="+1234567890"
                            disabled={loading}
                            required
                        />
                        <small className="form-text text-muted">
                            Phone number in E.164 format (e.g., +1234567890)
                        </small>
                    </FormGroup>

                    <FormGroup>
                        <Label for="channelTwilioSid">Twilio SID</Label>
                        <Input
                            type="text"
                            id="channelTwilioSid"
                            value={twilioSid}
                            onChange={(e) => setTwilioSid(e.target.value)}
                            placeholder="Enter Twilio Account SID"
                            disabled={loading}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="channelTwilioToken">Twilio Auth Token</Label>
                        <Input
                            type="password"
                            id="channelTwilioToken"
                            value={twilioAuthToken}
                            onChange={(e) => setTwilioAuthToken(e.target.value)}
                            placeholder="Enter Twilio Auth Token"
                            disabled={loading}
                        />
                        {isEdit && (
                            <small className="form-text text-muted">
                                Leave blank to keep existing credentials
                            </small>
                        )}
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle} disabled={loading}>
                        Cancel
                    </Button>
                    <Button color="primary" type="submit" disabled={loading}>
                        {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    )
}
